import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { CreditCard, Lock, ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { api, asError } from "@/api/client";
import { Steps } from "./Checkout";

export default function Payment() {
  const { items, subtotal, shipping, total, clear } = useCart();
  const nav = useNavigate();
  const [method, setMethod] = useState("card");
  const [card, setCard] = useState({ number: "", name: "", expiry: "", cvv: "" });
  const [processing, setProcessing] = useState(false);

  if (!items.length) return (
    <div className="bg-[var(--bg)] pt-[110px] pb-20 min-h-screen text-center">
      <h1 className="font-serif-display text-3xl mt-20 font-semibold">Your cart is empty.</h1>
      <Link to="/products" className="btn-primary mt-6 inline-flex">Shop Now</Link>
    </div>
  );

  const pay = async (e) => {
    e.preventDefault();
    if (method === "card") {
      if (!card.number || card.number.replace(/\s/g, "").length < 12 || !card.name || !card.expiry || !card.cvv) {
        toast.error("Please fill all card details."); return;
      }
    }
    const shippingData = JSON.parse(sessionStorage.getItem("nt_shipping") || "null");
    if (!shippingData) {
      toast.error("Shipping details missing. Please redo checkout.");
      nav("/checkout");
      return;
    }
    setProcessing(true);
    try {
      // Mock payment delay so the UX still feels like processing
      await new Promise((r) => setTimeout(r, 1200));
      const payload = {
        items: items.map((i) => ({
          id: i.id,
          name: i.name,
          image: i.image,
          weight: i.weight,
          price: typeof i.price === "string" ? parseInt(String(i.price).replace(/[^\d]/g, ""), 10) : i.price,
          qty: i.qty,
        })),
        shipping_address: shippingData,
        method,
      };
      const { data: order } = await api.post("/orders", payload);
      sessionStorage.setItem("nt_last_order", JSON.stringify(order));
      clear();
      nav("/checkout/success");
    } catch (err) {
      toast.error(asError(err));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div data-testid="page-payment" className="bg-[var(--bg)] pt-[110px] md:pt-[120px] pb-20 min-h-screen">
      <div className="max-w-[1280px] mx-auto px-4 md:px-10">
        <Steps current={2} />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-8">
          <form onSubmit={pay} data-testid="payment-form" className="lg:col-span-7 card-soft p-6 md:p-8">
            <h2 className="font-serif-display text-2xl font-semibold text-[var(--ink)] mb-5">Payment Method</h2>

            <div className="grid grid-cols-3 gap-2 mb-6">
              {[
                { id: "card", label: "Card" },
                { id: "upi", label: "UPI" },
                { id: "cod", label: "Cash on Delivery" },
              ].map((m) => (
                <button
                  key={m.id}
                  type="button"
                  data-testid={`pay-method-${m.id}`}
                  onClick={() => setMethod(m.id)}
                  className={`px-3 py-3 rounded-md border text-xs uppercase tracking-widest font-semibold transition ${
                    method === m.id ? "bg-[var(--green)] text-white border-[var(--green)]" : "bg-white text-[var(--ink-2)] border-[var(--line)] hover:border-[var(--green)]"
                  }`}
                >{m.label}</button>
              ))}
            </div>

            {method === "card" && (
              <div className="space-y-4">
                <Field testId="card-number" label="Card number" placeholder="1234 5678 9012 3456" value={card.number} onChange={(v) => setCard({ ...card, number: v.replace(/[^\d ]/g, "").slice(0, 19) })} />
                <Field testId="card-name" label="Name on card" value={card.name} onChange={(v) => setCard({ ...card, name: v })} />
                <div className="grid grid-cols-2 gap-4">
                  <Field testId="card-expiry" label="Expiry (MM/YY)" value={card.expiry} onChange={(v) => setCard({ ...card, expiry: v.slice(0, 5) })} />
                  <Field testId="card-cvv" label="CVV" type="password" value={card.cvv} onChange={(v) => setCard({ ...card, cvv: v.replace(/\D/g, "").slice(0, 4) })} />
                </div>
              </div>
            )}
            {method === "upi" && (
              <div className="space-y-4">
                <Field testId="upi-id" label="UPI ID" placeholder="yourname@bank" value={card.name} onChange={(v) => setCard({ ...card, name: v })} />
                <p className="text-xs text-[var(--ink-3)]">You'll receive a collect request on your UPI app to approve this ₹{total} payment.</p>
              </div>
            )}
            {method === "cod" && (
              <div className="bg-[var(--bg-cream)] border border-[var(--line-soft)] p-4 rounded-md">
                <p className="text-sm text-[var(--ink-2)]">Pay <strong className="text-[var(--green)]">₹{total}</strong> in cash to the delivery agent when your order arrives.</p>
              </div>
            )}

            <div className="mt-7 flex flex-wrap items-center justify-between gap-3">
              <Link to="/checkout" data-testid="payment-back" className="inline-flex items-center gap-2 text-sm text-[var(--green)] font-semibold link-underline"><ArrowLeft size={14} /> Back to Shipping</Link>
              <button type="submit" data-testid="payment-pay" disabled={processing} className="btn-primary disabled:opacity-60">
                {processing ? "Processing…" : <>Pay ₹{total} <ArrowRight size={14} /></>}
              </button>
            </div>
            <p className="kbd text-[0.6rem] text-[var(--ink-3)] flex items-center gap-2 mt-3"><Lock size={11} /> Demo payment — no real charge.</p>
          </form>

          <aside className="lg:col-span-5">
            <div className="card-soft p-6 sticky top-[100px]">
              <h3 className="font-serif-display text-xl font-semibold mb-5">Order Total</h3>
              <Row label="Subtotal" value={`₹${subtotal}`} />
              <Row label="Shipping" value={shipping === 0 ? "Free" : `₹${shipping}`} />
              <div className="flex justify-between pt-4 mt-4 border-t border-[var(--line-soft)]"><span className="font-semibold">Total</span><span className="font-serif-display text-2xl font-semibold text-[var(--green)]">₹{total}</span></div>
              <div className="mt-6 flex items-center gap-2 text-xs text-[var(--ink-3)]"><CheckCircle2 size={14} className="text-[var(--gold)]" /> 100% secure (demo)</div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function Field({ testId, label, value, onChange, type = "text", placeholder }) {
  return (
    <div className="flex flex-col">
      <label className="kbd text-[var(--ink-3)] mb-1.5 text-[0.6rem]">{label}</label>
      <input data-testid={testId} type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className="bg-white border border-[var(--line)] rounded-md px-4 py-2.5 text-sm focus:border-[var(--green)] focus:outline-none" />
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between text-sm py-1.5"><span className="text-[var(--ink-2)]">{label}</span><span className="font-semibold">{value}</span></div>;
}
